import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccess, homeForRole } from "@/lib/roles";
import { navAllowed, pathAllowed } from "@/lib/permissions";
import {
  LayoutDashboard, ShoppingCart, ClipboardList, Grid3x3, UtensilsCrossed,
  Boxes, Truck, Users, CalendarClock, BarChart3, Settings, LogOut, UserCog, ExternalLink,
  ListChecks, Fingerprint, Bike, MonitorPlay, Bus, Star, Trash2, ClipboardCheck, HandPlatter,
  Bell, Ticket, Landmark, MessageSquare, Building2, FileSpreadsheet, Printer, MessageCircle,
  Tag, Radio, Soup, Lock, ArrowRightLeft, FileInput, Shield, MapPinned, BadgeIndianRupee,
} from "lucide-react";
import OjentLogo from "@/components/OjentLogo";

const FULL_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/waiter", label: "Waiter Portal", icon: HandPlatter },
  { to: "/kitchen", label: "Kitchen Portal", icon: MonitorPlay },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck },
  { to: "/calls", label: "Waiter Calls", icon: Bell },
  { to: "/tokens-desk", label: "Tokens", icon: Ticket },
  { to: "/reconcile", label: "Reconcile", icon: Landmark },
  { to: "/day-end", label: "Day End", icon: Lock },
  { to: "/sms", label: "SMS", icon: MessageSquare },
  { to: "/pos", label: "POS Billing", icon: ShoppingCart },
  { to: "/kds", label: "Kitchen Display", icon: MonitorPlay },
  { to: "/orders", label: "Orders", icon: ClipboardList },
  { to: "/tables", label: "Tables", icon: Grid3x3 },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/waste", label: "Waste Log", icon: Trash2 },
  { to: "/vendors", label: "Vendors", icon: Truck },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/reviews", label: "Reviews", icon: Star },
  { to: "/reservations", label: "Reservations", icon: CalendarClock },
  { to: "/channels", label: "Channels", icon: Bike },
  { to: "/delivery", label: "Delivery", icon: Bus },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/attendance", label: "Attendance", icon: Fingerprint },
  { to: "/reports", label: "Reports", icon: BarChart3 },
];

const ENTERPRISE_NAV = [
  { to: "/chain", label: "Chain", icon: Building2 },
  { to: "/accounting", label: "Accounting", icon: FileSpreadsheet },
  { to: "/printers", label: "Printers", icon: Printer },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/brands", label: "Brands", icon: Tag },
  { to: "/aggregator", label: "Aggregator", icon: Radio },
  { to: "/mess", label: "Mess", icon: Soup },
  { to: "/transfers", label: "Transfers", icon: ArrowRightLeft },
  { to: "/purchase-invoices", label: "Purchases", icon: FileInput },
  { to: "/floor-setup", label: "Areas & Tills", icon: MapPinned },
  { to: "/permissions", label: "Permissions", icon: Shield },
  { to: "/subscription", label: "Subscription", icon: BadgeIndianRupee },
];

const ADMIN_NAV = [
  { to: "/staff", label: "Staff", icon: UserCog },
  { to: "/settings", label: "Settings", icon: Settings },
];

function navFor(role) {
  if (role === "waiter") {
    return [
      { to: "/waiter", label: "Waiter Portal", icon: HandPlatter },
      { to: "/calls", label: "Waiter Calls", icon: Bell },
    ];
  }
  if (role === "kitchen") return [{ to: "/kitchen", label: "Kitchen Portal", icon: MonitorPlay }];
  const items = FULL_NAV.filter((n) => n.to !== "/kds");
  if (role === "staff") {
    return items.filter((n) => !["/waiter", "/kitchen", "/approvals"].includes(n.to));
  }
  return items;
}

export default function DashboardLayout() {
  const { user, logout, isAdmin, isManager, switchOutlet } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const { data: orgData } = useQuery({
    queryKey: ["org"],
    queryFn: async () => (await api.get("/org")).data,
    enabled: !!(isAdmin || isManager),
    staleTime: 60_000,
  });
  const { data: myPerms } = useQuery({
    queryKey: ["permissions-me"],
    queryFn: async () => (await api.get("/permissions/me")).data,
    staleTime: 60_000,
  });
  const outlets = orgData?.outlets || [];
  const showSwitcher = outlets.length > 1;

  useEffect(() => {
    if (!user) return;
    if (!canAccess(user.role, location.pathname)) {
      navigate(homeForRole(user.role), { replace: true });
      return;
    }
    if (myPerms && !pathAllowed(location.pathname, myPerms)) {
      navigate(homeForRole(user.role), { replace: true });
    }
  }, [user, location.pathname, navigate, myPerms]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all active:scale-[0.98] ${
      isActive ? "bg-[var(--kul-primary)] text-white shadow-sm" : "text-[#3a3a3c] hover:bg-black/[0.05]"
    }`;

  const nav = navFor(user?.role).filter((n) => navAllowed(n.to, myPerms));
  const enterpriseNav = ENTERPRISE_NAV.filter((n) => navAllowed(n.to, myPerms));

  const onSwitchOutlet = async (restaurantId) => {
    if (!restaurantId || restaurantId === (orgData?.current_restaurant_id || user?.restaurant_id)) return;
    try {
      await switchOutlet(restaurantId);
      toast.success("Switched outlet");
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--kul-app-bg)]">
      <aside className="w-60 shrink-0 glass flex flex-col relative border-r border-black/5" data-testid="sidebar">
        <div className="px-5 py-4">
          <OjentLogo markSize={36} showTagline />
        </div>

        {showSwitcher && (
          <div className="px-3 pb-2">
            <select
              value={orgData?.current_restaurant_id || user?.restaurant_id || ""}
              onChange={(e) => onSwitchOutlet(e.target.value)}
              className="w-full h-9 rounded-xl border border-black/10 bg-white/80 px-2 text-[12px] font-medium"
              data-testid="outlet-switcher"
            >
              {outlets.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkClass}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <n.icon className="w-[17px] h-[17px]" /> {n.label}
            </NavLink>
          ))}
          {(isAdmin || isManager) && (
            <>
              <div className="px-3 pt-4 pb-1 text-[10px] text-[#aeaeb2] tracking-[0.14em] uppercase">Enterprise</div>
              {enterpriseNav.map((n) => (
                <NavLink key={n.to} to={n.to} className={linkClass}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <n.icon className="w-[17px] h-[17px]" /> {n.label}
                </NavLink>
              ))}
              <div className="px-3 pt-4 pb-1 text-[10px] text-[#aeaeb2] tracking-[0.14em] uppercase">Admin</div>
              {(isAdmin ? ADMIN_NAV : ADMIN_NAV.filter((n) => n.to === "/settings")).map((n) => (
                <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.label.toLowerCase()}`}>
                  <n.icon className="w-[17px] h-[17px]" /> {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-black/5 space-y-1">
          {isAdmin && (
            <a href={user?.restaurant?.slug ? `/r/${user.restaurant.slug}/order` : "/order"} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] text-[#3a3a3c] hover:bg-black/[0.05] transition-colors"
              data-testid="view-storefront-link">
              <ExternalLink className="w-[17px] h-[17px]" /> Online Store
            </a>
          )}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-[var(--kul-primary)]/15 text-[var(--kul-primary)] flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-[#1d1d1f] font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-[#86868b] capitalize truncate">{user?.restaurant?.name || user?.role}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="text-[#86868b] hover:text-[var(--kul-danger)] transition-colors">
              <LogOut className="w-[17px] h-[17px]" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
